import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './ManageSubcategories.css';

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
  icon: string;
  order?: number;
  commissionRate?: number;
  active?: boolean;
}

interface Category {
  id: string;
  name: string;
  icon?: string;
  order?: number;
}

interface GroupedSubcategories {
  [categoryId: string]: Subcategory[];
}

interface NewSubcategory {
  name: string;
  icon: string;
  commissionRate: number;
}

const ManageSubcategories: React.FC = () => {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [updating, setUpdating] = useState<string | null>(null);
  
  // States for grouping
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  
  // States for adding new subcategory
  const [addingToCategory, setAddingToCategory] = useState<string | null>(null);
  const [newSubcategory, setNewSubcategory] = useState<NewSubcategory>({
    name: '',
    icon: '',
    commissionRate: 0
  });
  const [addingSubcategory, setAddingSubcategory] = useState(false);
  
  // States for inline editing
  const [editingSubcategory, setEditingSubcategory] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'name' | 'icon' | 'commissionRate' | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [updatingField, setUpdatingField] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      console.log('Bắt đầu tải danh sách ngành nghề...');
      
      // Thử fetch từ collection 'services' trước (như đã mô tả trong user query)
      let categoriesQuery;
      let querySnapshot;

      try {
        categoriesQuery = query(
          collection(db, 'services'),
          orderBy('order', 'asc')
        );
        console.log('Thử query services với orderBy order...');
        querySnapshot = await getDocs(categoriesQuery);
      } catch (indexError) {
        console.warn('Query services với orderBy thất bại, thử query đơn giản:', indexError);
        try {
          categoriesQuery = collection(db, 'services');
          console.log('Thử query services đơn giản...');
          querySnapshot = await getDocs(categoriesQuery);
        } catch (servicesError) {
          console.warn('Collection services không tồn tại, thử categories:', servicesError);
          // Fallback to categories collection
          try {
            categoriesQuery = query(
              collection(db, 'categories'),
              orderBy('order', 'asc')
            );
            console.log('Thử query categories với orderBy order...');
            querySnapshot = await getDocs(categoriesQuery);
          } catch (categoriesIndexError) {
            console.warn('Query categories với orderBy thất bại, thử query đơn giản:', categoriesIndexError);
            categoriesQuery = collection(db, 'categories');
            console.log('Thử query categories đơn giản...');
            querySnapshot = await getDocs(categoriesQuery);
          }
        }
      }

      const categoriesData: Category[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Category doc:', doc.id, data);
        
        categoriesData.push({
          id: doc.id,
          name: data.name || '',
          icon: data.icon || '',
          order: data.order || 0
        });
      });

      // Sắp xếp thủ công theo order nếu cần
      if (categoriesData.length > 0) {
        categoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${categoriesData.length} ngành nghề`);
      setCategories(categoriesData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách ngành nghề:', error);
      setCategories([]);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách dịch vụ...');

      let subcategoriesQuery;
      let querySnapshot;

      try {
        // Thử query với orderBy theo trường order trước
        subcategoriesQuery = query(
          collection(db, 'subcategories'),
          orderBy('order', 'asc')
        );
        console.log('Thử query với orderBy order...');
        querySnapshot = await getDocs(subcategoriesQuery);
      } catch (indexError) {
        console.warn('Query với orderBy thất bại, thử query đơn giản:', indexError);
        
        // Nếu lỗi index, thử query đơn giản hơn
        subcategoriesQuery = collection(db, 'subcategories');
        console.log('Thử query đơn giản...');
        querySnapshot = await getDocs(subcategoriesQuery);
      }

      const subcategoriesData: Subcategory[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Subcategory doc:', doc.id, data);
        
        subcategoriesData.push({
          id: doc.id,
          name: data.name || '',
          categoryId: data.categoryId || '',
          icon: data.icon || '',
          order: data.order || 0,
          commissionRate: data.commissionRate || 0,
          active: data.active ?? true // Mặc định là true nếu không có giá trị
        });
      });

      // Sắp xếp thủ công theo order nếu cần
      if (subcategoriesData.length > 0) {
        subcategoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${subcategoriesData.length} dịch vụ`);
      setSubcategories(subcategoriesData);
      setFilteredSubcategories(subcategoriesData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách dịch vụ:', error);
      
      let errorMessage = 'Không thể tải danh sách dịch vụ';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || categoryId || 'Không xác định';
  };

  const applyFilters = (subcategoriesToFilter: Subcategory[]) => {
    let filtered = subcategoriesToFilter;

    // Filter theo tên, icon, categoryId hoặc tên category
    if (nameFilter.trim()) {
      filtered = filtered.filter(subcategory => {
        const categoryName = getCategoryName(subcategory.categoryId);
        return (
          subcategory.name.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          subcategory.icon.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          subcategory.categoryId.toLowerCase().includes(nameFilter.trim().toLowerCase()) ||
          categoryName.toLowerCase().includes(nameFilter.trim().toLowerCase())
        );
      });
    }

    return filtered;
  };

  const groupSubcategoriesByCategory = (subcategoriesToGroup: Subcategory[]): GroupedSubcategories => {
    const grouped: GroupedSubcategories = {};
    
    subcategoriesToGroup.forEach(subcategory => {
      const categoryId = subcategory.categoryId || 'unknown';
      if (!grouped[categoryId]) {
        grouped[categoryId] = [];
      }
      grouped[categoryId].push(subcategory);
    });

    // Sắp xếp các subcategories trong mỗi group theo order
    Object.keys(grouped).forEach(categoryId => {
      grouped[categoryId].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return grouped;
  };

  const toggleGroupCollapse = (categoryId: string) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(categoryId)) {
      newCollapsed.delete(categoryId);
    } else {
      newCollapsed.add(categoryId);
    }
    setCollapsedGroups(newCollapsed);
  };

  const startAddingSubcategory = (categoryId: string) => {
    setAddingToCategory(categoryId);
    setNewSubcategory({
      name: '',
      icon: '',
      commissionRate: 0
    });
  };

  const cancelAddingSubcategory = () => {
    setAddingToCategory(null);
    setNewSubcategory({
      name: '',
      icon: '',
      commissionRate: 0
    });
  };

  const saveNewSubcategory = async (categoryId: string) => {
    // Validation
    if (!newSubcategory.name.trim()) {
      alert('Tên dịch vụ không thể để trống');
      return;
    }

    if (!newSubcategory.icon.trim()) {
      alert('Icon không thể để trống');
      return;
    }

    if (isNaN(newSubcategory.commissionRate) || newSubcategory.commissionRate < 0 || newSubcategory.commissionRate > 100) {
      alert('Tỷ lệ hoa hồng phải là số từ 0 đến 100');
      return;
    }

    try {
      setAddingSubcategory(true);

      // Tính toán order mới cho subcategory
      const categorySubcategories = subcategories.filter(sub => sub.categoryId === categoryId);
      const maxOrder = categorySubcategories.length > 0 
        ? Math.max(...categorySubcategories.map(sub => sub.order || 0))
        : 0;

      // Tạo document mới
      const newSubcategoryData = {
        name: newSubcategory.name.trim(),
        categoryId: categoryId,
        icon: newSubcategory.icon.trim(),
        order: maxOrder + 1,
        commissionRate: newSubcategory.commissionRate
      };

      console.log('Tạo subcategory mới:', newSubcategoryData);
      
      const docRef = await addDoc(collection(db, 'subcategories'), newSubcategoryData);
      
      // Cập nhật state local
      const newSubcategoryWithId: Subcategory = {
        id: docRef.id,
        ...newSubcategoryData
      };

      const updatedSubcategories = [...subcategories, newSubcategoryWithId];
      
      // Sắp xếp lại theo order
      updatedSubcategories.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setSubcategories(updatedSubcategories);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedSubcategories);
      setFilteredSubcategories(filtered);

      // Reset form
      cancelAddingSubcategory();

      console.log('Đã tạo subcategory mới thành công:', docRef.id);

    } catch (error) {
      console.error('Lỗi khi tạo subcategory mới:', error);
      setError('Tạo dịch vụ mới thất bại');
    } finally {
      setAddingSubcategory(false);
    }
  };

  const updateSubcategoryOrder = async (subcategoryId: string, newOrder: number) => {
    try {
      setUpdating(subcategoryId);
      const subcategoryRef = doc(db, 'subcategories', subcategoryId);
      await updateDoc(subcategoryRef, {
        order: newOrder
      });

      // Cập nhật state local
      const updatedSubcategories = subcategories.map(subcategory =>
        subcategory.id === subcategoryId
          ? { ...subcategory, order: newOrder }
          : subcategory
      );
      
      // Sắp xếp lại theo order
      updatedSubcategories.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setSubcategories(updatedSubcategories);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedSubcategories);
      setFilteredSubcategories(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật thứ tự:', error);
      setError('Cập nhật thứ tự thất bại');
    } finally {
      setUpdating(null);
    }
  };

  const startEditing = (subcategory: Subcategory, field: 'name' | 'icon' | 'commissionRate') => {
    setEditingSubcategory(subcategory.id);
    setEditingField(field);
    
    if (field === 'name') {
      setEditingValue(subcategory.name);
    } else if (field === 'icon') {
      setEditingValue(subcategory.icon);
    } else if (field === 'commissionRate') {
      setEditingValue((subcategory.commissionRate || 0).toString());
    }
  };

  const cancelEditing = () => {
    setEditingSubcategory(null);
    setEditingField(null);
    setEditingValue('');
  };

  const saveSubcategory = async (subcategoryId: string) => {
    if (!editingField) return;
    
    if (editingField === 'name' && !editingValue.trim()) {
      alert('Tên dịch vụ không thể để trống');
      return;
    }
    
    if (editingField === 'icon' && !editingValue.trim()) {
      alert('Icon không thể để trống');
      return;
    }

    if (editingField === 'commissionRate') {
      const rate = parseFloat(editingValue);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        alert('Tỷ lệ hoa hồng phải là số từ 0 đến 100');
        return;
      }
    }

    try {
      setUpdatingField(subcategoryId);
      const subcategoryRef = doc(db, 'subcategories', subcategoryId);
      
      const updateData: any = {};
      if (editingField === 'commissionRate') {
        updateData[editingField] = parseFloat(editingValue);
      } else {
        updateData[editingField] = editingValue.trim();
      }
      
      await updateDoc(subcategoryRef, updateData);

      // Cập nhật state local
      const updatedSubcategories = subcategories.map(subcategory =>
        subcategory.id === subcategoryId
          ? { 
              ...subcategory, 
              [editingField]: editingField === 'commissionRate' 
                ? parseFloat(editingValue) 
                : editingValue.trim() 
            }
          : subcategory
      );
      
      setSubcategories(updatedSubcategories);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedSubcategories);
      setFilteredSubcategories(filtered);

      // Reset editing state
      setEditingSubcategory(null);
      setEditingField(null);
      setEditingValue('');

    } catch (error) {
      console.error('Lỗi khi cập nhật:', error);
      setError('Cập nhật thất bại');
    } finally {
      setUpdatingField(null);
    }
  };

  const toggleActive = async (subcategoryId: string, currentActive: boolean) => {
    try {
      setUpdatingField(subcategoryId);
      const subcategoryRef = doc(db, 'subcategories', subcategoryId);
      await updateDoc(subcategoryRef, {
        active: !currentActive
      });

      // Cập nhật state local
      const updatedSubcategories = subcategories.map(subcategory =>
        subcategory.id === subcategoryId
          ? { ...subcategory, active: !currentActive }
          : subcategory
      );
      
      setSubcategories(updatedSubcategories);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedSubcategories);
      setFilteredSubcategories(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái hiển thị:', error);
      setError('Cập nhật trạng thái hiển thị thất bại');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, subcategoryId: string) => {
    if (e.key === 'Enter') {
      saveSubcategory(subcategoryId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleNewSubcategoryKeyPress = (e: React.KeyboardEvent, categoryId: string) => {
    if (e.key === 'Enter') {
      saveNewSubcategory(categoryId);
    } else if (e.key === 'Escape') {
      cancelAddingSubcategory();
    }
  };

  // Effect để fetch data lần đầu
  useEffect(() => {
    const fetchData = async () => {
      await fetchCategories();
      await fetchSubcategories();
    };
    fetchData();
  }, []);

  // Effect để apply filters khi subcategories, categories hoặc filters thay đổi
  useEffect(() => {
    if (subcategories.length > 0) {
      const filtered = applyFilters(subcategories);
      setFilteredSubcategories(filtered);
    }
  }, [subcategories, categories, nameFilter]);

  if (loading) {
    return (
      <div className="manage-subcategories">
        <div className="page-header">
          <h2>Quản lý dịch vụ</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả dịch vụ theo ngành nghề</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  const groupedSubcategories = groupSubcategoriesByCategory(filteredSubcategories);
  const sortedCategoryIds = Object.keys(groupedSubcategories).sort((a, b) => {
    const categoryA = categories.find(cat => cat.id === a);
    const categoryB = categories.find(cat => cat.id === b);
    const orderA = categoryA?.order || 0;
    const orderB = categoryB?.order || 0;
    return orderA - orderB;
  });

  return (
    <div className="manage-subcategories">
      <div className="page-header">
        <h2>Quản lý dịch vụ</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả dịch vụ theo ngành nghề</p>
        <p className="edit-instruction">💡 Click vào các trường hoặc icon ✏️ để chỉnh sửa. Nhấn Enter hoặc click ra ngoài để lưu, Escape để hủy.</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="name-filter">Tìm kiếm:</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Tìm theo tên, icon, ngành nghề..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <button onClick={() => {
          fetchCategories();
          fetchSubcategories();
        }} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchCategories();
              fetchSubcategories();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="grouped-container">
        {sortedCategoryIds.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {subcategories.length === 0 ? 'Không có dịch vụ nào' : 'Không tìm thấy dịch vụ phù hợp'}
            </div>
          </div>
        ) : (
          sortedCategoryIds.map((categoryId) => {
            const categorySubcategories = groupedSubcategories[categoryId];
            const categoryName = getCategoryName(categoryId);
            const isCollapsed = collapsedGroups.has(categoryId);
            
            return (
              <div key={categoryId} className="category-group">
                <div 
                  className="category-header"
                  onClick={() => toggleGroupCollapse(categoryId)}
                >
                  <div className="category-info">
                    <h3 className="category-title">{categoryName}</h3>
                    <span className="category-count">({categorySubcategories.length} dịch vụ)</span>
                  </div>
                  <div className="header-actions">
                    <button
                      className="add-service-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        startAddingSubcategory(categoryId);
                      }}
                      title="Thêm dịch vụ mới"
                      disabled={addingToCategory !== null}
                    >
                      + Thêm dịch vụ
                    </button>
                    <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                  </div>
                </div>
                
                {!isCollapsed && (
                  <div className="table-container">
                    {addingToCategory === categoryId && (
                      <div className="add-subcategory-form">
                        <div className="form-row">
                          <input
                            type="text"
                            value={newSubcategory.name}
                            onChange={(e) => setNewSubcategory({...newSubcategory, name: e.target.value})}
                            onKeyDown={(e) => handleNewSubcategoryKeyPress(e, categoryId)}
                            placeholder="Tên dịch vụ..."
                            className="form-input"
                            disabled={addingSubcategory}
                            autoFocus
                          />
                          <input
                            type="text"
                            value={newSubcategory.icon}
                            onChange={(e) => setNewSubcategory({...newSubcategory, icon: e.target.value})}
                            onKeyDown={(e) => handleNewSubcategoryKeyPress(e, categoryId)}
                            placeholder="Icon..."
                            className="form-input"
                            disabled={addingSubcategory}
                          />
                          <input
                            type="number"
                            value={newSubcategory.commissionRate}
                            onChange={(e) => setNewSubcategory({...newSubcategory, commissionRate: parseFloat(e.target.value) || 0})}
                            onKeyDown={(e) => handleNewSubcategoryKeyPress(e, categoryId)}
                            placeholder="Tỷ lệ sàn thu (%)"
                            className="form-input"
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={addingSubcategory}
                          />
                          <div className="form-actions">
                            <button
                              onClick={() => saveNewSubcategory(categoryId)}
                              disabled={addingSubcategory}
                              className="save-button"
                            >
                              {addingSubcategory ? 'Đang lưu...' : 'Lưu'}
                            </button>
                            <button
                              onClick={cancelAddingSubcategory}
                              disabled={addingSubcategory}
                              className="cancel-button"
                            >
                              Hủy
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <table className="subcategories-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Tên dịch vụ</th>
                          <th>Icon</th>
                          <th>Sàn thu (%)</th>
                          <th>Sắp xếp</th>
                          <th>Hiển thị</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categorySubcategories.map((subcategory, index) => (
                          <tr key={subcategory.id}>
                            <td className="order-cell">
                              <span className="order-display">{index + 1}</span>
                            </td>
                            <td className="subcategory-name">
                              {editingSubcategory === subcategory.id && editingField === 'name' ? (
                                <div className="field-edit-container">
                                  <input
                                    type="text"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => handleKeyPress(e, subcategory.id)}
                                    onBlur={() => saveSubcategory(subcategory.id)}
                                    className="field-edit-input"
                                    autoFocus
                                    disabled={updatingField === subcategory.id}
                                  />
                                  {updatingField === subcategory.id && (
                                    <span className="updating">Đang lưu...</span>
                                  )}
                                </div>
                              ) : (
                                <div className="editable-container">
                                  <strong
                                    className="field-text editable"
                                    onClick={() => startEditing(subcategory, 'name')}
                                    title="Click để chỉnh sửa"
                                  >
                                    {subcategory.name}
                                  </strong>
                                  <button
                                    className="edit-icon-btn"
                                    onClick={() => startEditing(subcategory, 'name')}
                                    title="Chỉnh sửa tên"
                                  >
                                    ✏️
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="subcategory-icon">
                              {editingSubcategory === subcategory.id && editingField === 'icon' ? (
                                <div className="field-edit-container">
                                  <input
                                    type="text"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => handleKeyPress(e, subcategory.id)}
                                    onBlur={() => saveSubcategory(subcategory.id)}
                                    className="field-edit-input"
                                    autoFocus
                                    disabled={updatingField === subcategory.id}
                                  />
                                  {updatingField === subcategory.id && (
                                    <span className="updating">Đang lưu...</span>
                                  )}
                                </div>
                              ) : (
                                <div className="editable-container">
                                  <span
                                    className="field-text icon-text editable"
                                    onClick={() => startEditing(subcategory, 'icon')}
                                    title="Click để chỉnh sửa"
                                  >
                                    {subcategory.icon || 'Chưa có icon'}
                                  </span>
                                  <button
                                    className="edit-icon-btn"
                                    onClick={() => startEditing(subcategory, 'icon')}
                                    title="Chỉnh sửa icon"
                                  >
                                    ✏️
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="subcategory-commission">
                              {editingSubcategory === subcategory.id && editingField === 'commissionRate' ? (
                                <div className="field-edit-container">
                                  <input
                                    type="number"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => handleKeyPress(e, subcategory.id)}
                                    onBlur={() => saveSubcategory(subcategory.id)}
                                    className="field-edit-input"
                                    autoFocus
                                    disabled={updatingField === subcategory.id}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                  />
                                  {updatingField === subcategory.id && (
                                    <span className="updating">Đang lưu...</span>
                                  )}
                                </div>
                              ) : (
                                <div className="editable-container">
                                  <span
                                    className="field-text commission-text editable"
                                    onClick={() => startEditing(subcategory, 'commissionRate')}
                                    title="Click để chỉnh sửa"
                                  >
                                    {(subcategory.commissionRate || 0).toFixed(1)}%
                                  </span>
                                  <button
                                    className="edit-icon-btn"
                                    onClick={() => startEditing(subcategory, 'commissionRate')}
                                    title="Chỉnh sửa tỷ lệ sàn thu"
                                  >
                                    ✏️
                                  </button>
                                </div>
                              )}
                            </td>
                            <td>
                              <input
                                type="number"
                                value={subcategory.order || 0}
                                onChange={(e) => updateSubcategoryOrder(subcategory.id, parseInt(e.target.value) || 0)}
                                disabled={updating === subcategory.id}
                                className="order-input"
                                min="0"
                                placeholder="Thứ tự"
                              />
                              {updating === subcategory.id && (
                                <span className="updating">Đang cập nhật...</span>
                              )}
                            </td>
                            <td className="subcategory-active">
                              <div className="toggle-container">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={subcategory.active ?? true}
                                    onChange={() => toggleActive(subcategory.id, subcategory.active ?? true)}
                                    disabled={updatingField === subcategory.id}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                                {updatingField === subcategory.id && (
                                  <span className="updating">Đang cập nhật...</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Row để thêm dịch vụ mới */}
                        {/* This row is now handled by the form above */}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ManageSubcategories; 